import { faMusic } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { selectState as selectStateAuth } from "authentication/main.slice";
import Loader from "components/Loader/Loader";
import Popover from "components/Popover/Popover";
import ToolboxButton, { ToolboxButtonIcon } from "Dashboard/components/Buttons/ToolboxButton";
import { RESOURCES_MODULE_NAME } from "Dashboard/scenes/Resources/domain/ResourcesModule";
import { unitNameLabel } from "Dashboard/scenes/Resources/helpers/resourceUtil";
import { useAppDispatch } from "hooks/hooks";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { FormatDateShort } from "utils/dateUtils";
import { FormatNumberOrNull, FormatNumberWithUnitOrNull, getAdjustedFormatForUnit } from "utils/numberUtils";
import { resourceMaintenanceTypeDictionary } from "utils/translationDictionary";
import {
  clearConsumablesMap,
  getServices,
  requestDeletingServiceOrRepair,
  selectState,
  setServiceRepairPopover,
  updateService,
  updateServiceTemporarily,
} from "../serviceAndRepair.slice";
import { selectResourceDetails } from "../../Details/details.slice";
import ServiceRepairPopover from "./ServiceRepairPopover";
import { IUpdateService } from "Dashboard/scenes/Resources/domain/IUpdateService";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import InfoTooltip from "components/Popup/InfoTooltip";
import DeleteServiceOrRepairModal from "./DeleteServiceOrRepairModal";

interface IProps {
  resourceId: number;
}

const ResourceServiceAndRepair = ({ resourceId }: IProps) => {
  const dispatch = useAppDispatch();

  const { loading, services, serviceRepairPopover } = useSelector(selectState);
  const resourceDetails = useSelector(selectResourceDetails);
  const isResourceActive = resourceDetails?.resource.status === "ACTIVE"
  const { user } = useSelector(selectStateAuth);

  const GetServices = useCallback(() => {
    dispatch(getServices(resourceId));
  }, [dispatch, resourceId]);

  useEffect(() => {
    if (resourceId) GetServices();
  }, [GetServices, resourceId]);

  const onChangeBasicField = (data: IUpdateService) => {
    dispatch(updateServiceTemporarily(data));
    updateBasicFieldWithDelay(data);
  };

  const updateBasicFieldWithDelay = useMemo(
    () => debounce(async (data: IUpdateService) => {
      try {
        await dispatch(updateService(data)).unwrap();
        toast.success("Izmena je uspešno zabeležena.");
      } catch (status: any) {
        if (status === 403) {
          toast.error("Neočekivana greška");
        } else if (status === 400) {
          toast.error("Neočekivana greška");
        }
      }
    }, 1000),
    [dispatch]
  );

  return (
    <div className="col-4 loading-container">
      {(!resourceId || loading) && <Loader />}
      <div className="resources-box-header">
        <ToolboxButton
          icon={ToolboxButtonIcon.Add}
          onClick={hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
            ? (e) => dispatch(
              setServiceRepairPopover({
                isOpen: true,
                top: e.clientY,
                left: e.clientX,
              })
            )
            : undefined
          }
        />
        <span className="title">Servis * Popravka * Maziva * Usluge</span>
        <span></span>
      </div>
      <div className="resources-box-sub-header">
        <span>Usluga / KM_H/ cena</span>
      </div>
      <div className="resources-box-list">
        <div className="resource-wrapp">
          {services &&
            services.map((service, index) =>
              service.consumables.map((cons) => (
                <div className="secondResource" key={index}>
                  <InfoTooltip
                    tooltip={`${resourceMaintenanceTypeDictionary[service.serviceType]} - ${cons.name}`}
                    classesProp="name bg-text-overflow"
                    element="span"
                  >
                    {resourceMaintenanceTypeDictionary[service.serviceType]} - {cons.name}
                  </InfoTooltip>
                  <span className="date">
                    {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                      ? (<DatePicker
                        onChange={(date) =>
                          onChangeBasicField({ resourceId: service.resource.resourceId, serviceId: service.serviceId, serviceDoneAtDate: date ? date.toISOString().split("T")[0] : undefined })
                        }
                        showMonthDropdown
                        showYearDropdown
                        dateFormat="dd.MM.yy"
                        className="date"
                        selected={service.serviceDoneAtDate ? new Date(service.serviceDoneAtDate) : null}
                      />)
                      : FormatDateShort(service.serviceDoneAtDate)
                    }
                  </span>
                  <span className="unit1">{unitNameLabel(service.resourceUsageUnit)}</span>
                  <span className="number1">{FormatNumberOrNull(service.serviceDoneAtUsageAmount, getAdjustedFormatForUnit(service.resourceUsageUnit?.name))}</span>
                  <span className="unit2">{unitNameLabel(cons.quantityUnit)}</span>
                  <span className="number2">{FormatNumberOrNull(cons.quantityAmount, getAdjustedFormatForUnit(cons.quantityUnit?.name))}</span>
                  <input
                    type="text"
                    value={service.note || ""}
                    onChange={(e) => {
                      onChangeBasicField({ resourceId: service.resource.resourceId, serviceId: service.serviceId, note: e.target.value })
                    }}
                    className="note"
                    disabled={!(hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive)}
                  />
                  <span className="icons">
                    {service.hasAlarm && <FontAwesomeIcon icon={faMusic} />}
                    {false && hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                      ? (<ToolboxButton
                        icon={ToolboxButtonIcon.Delete}
                        tooltip="Obriši"
                        noCircle={true}
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(requestDeletingServiceOrRepair(service));
                        }}
                      />)
                      : undefined}
                  </span>
                </div>
              ))
            )}
          {services.length < 4 && <div className="secondResource"></div>}
          {services.length < 3 && <div className="secondResource"></div>}
          {services.length < 2 && <div className="secondResource"></div>}
          {services.length < 1 && <div className="secondResource"></div>}
        </div>
      </div>
      <div className="secondFooter">
        <span className="w60">nije urađeno {FormatNumberWithUnitOrNull(0, 'rsd')}</span>
      </div>
      <Popover
        isOpen={serviceRepairPopover.isOpen}
        onClose={() => {
          dispatch(setServiceRepairPopover({ isOpen: false }));
          dispatch(clearConsumablesMap());
        }}
        title="Servisi - Popravke"
        direction="left"
        arrowStyle={{ top: "243px" }}
        position={{
          top: -650,
          left: 510,
          offsetByWindowSizeY: true
        }}
      >
        <ServiceRepairPopover onModalChange={GetServices} />
      </Popover>
      <DeleteServiceOrRepairModal />
    </div>
  );
};

export default ResourceServiceAndRepair;
